import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { parameterize } from 'helpers/string';

import Container  from '@mui/material/Container';
import Grid       from '@mui/material/Grid';
import Button     from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Completion from 'components/LandingPage/Answers/Completion';
import Modal      from '@mui/material/Modal';
import useTheme   from '@mui/material/styles/useTheme';

import { Router } from '@reach/router';

import { Map } from 'immutable';

import RoomOutlined                 from '@mui/icons-material/RoomOutlined';
import BusinessCenter               from '@mui/icons-material/BusinessCenter';
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import Share                        from '@mui/icons-material/Share';
import CloseIcon                    from '@mui/icons-material/Close';

import ReactHtmlParser from 'react-html-parser';

import useMediaQuery from '@mui/material/useMediaQuery';

import CampaignRecord from 'records/campaign_record';
import JobRecord      from 'records/job_record';
import Company        from 'records/company';
import CookieConsent  from 'react-cookie-consent';

import HighlightNotification from 'components/App/Notifications/HighlightNotification';
import Display     from 'components/Generic/Display';
import ModalBox    from 'components/Generic/MUI/ModalBox';
import SocialShare from 'components/Generic/SocialShare';
import Header      from 'components/Layout/Header';
import Footer      from 'components/Layout/Footer';
import CampaignBanner from 'components/Layout/Banner';

import { SnackbarProvider }  from 'notistack';

import ApolloWrapper from './ApolloWrapper';
import Apply         from './Apply';
import Candidate     from './Candidate';

function Page(props) {
  const {
    uri, children, navigate, campaign, company, querystring, apply,
  } = props;

  const path  = props['*'];
  const { theme } = props;
  const { cookieNotification } = props.company.parameters;
  const { privacyPolicyLink } = props.company.parameters;

  const [open, setOpen] = useState(false);

  const [state] = useState(new Map());

  const job = state.get('job', new JobRecord(campaign.origin));

  let applyLocation = `${uri}/apply`;

  if (querystring.length > 0) {
    applyLocation = `${applyLocation}?${querystring}`;
  }

  const isScreenSM = useMediaQuery(theme.breakpoints.down('sm'));

  const showBanner = () => (
    path === ''
      || path === 'apply'
      || path === 'webview'
      || path.match(/^candidate\/[a-z0-9\-_=]+?\.[a-z0-9\-_=]+?\.[a-z0-9\-_=]+\/completed$/i)
      || (path.match(/^candidate\/[a-z0-9\-_=]+?\.[a-z0-9\-_=]+?\.[a-z0-9\-_=]+$/i) && campaign.isLongForm())
  );

  const JobInfo = () => {
    const jobtype = t(parameterize(job.jobType), { scope: 'eos.landing_page.text.job_type_options', defaultValue: '' });

    return (
      <Grid
        container
        item
        md={4}
        xs={12}
        sx={{
          [theme.breakpoints.down('sm')]: {
            ml: 2,
            mb: 2,
          },
        }}
      >
        <Grid container item xs={12} sx={{ alignItems: 'flex-start' }}>
          <Grid
            item
            xs={4}
            sx={{
              display: 'flex', justifyContent: 'flex-end', pt: 4, pr: 3,
            }}
          >
            <RoomOutlined color="primary" fontSize="large" />
          </Grid>
          <Grid item xs={8} sx={{ bgcolor: theme.palette.grey['100'], pt: 4, px: 4 }}>
            <Typography variant="body" color="primary" component="div" sx={{ fontWeight: 'bold' }}>
              {t('eos.landing_page.label.location')}
            </Typography>
            <Typography variant="body">
              {campaign.cityLocation()}
            </Typography>
          </Grid>
        </Grid>

        <Display if={jobtype}>
          <Grid container item xs={12} sx={{ alignItems: 'flex-start' }}>
            <Grid
              item
              xs={4}
              sx={{
                display: 'flex', justifyContent: 'flex-end', pt: 2, pr: 3,
              }}
            >
              <BusinessCenter color="primary" fontSize="large" />
            </Grid>
            <Grid item xs={8} sx={{ bgcolor: theme.palette.grey['100'], pt: 2, px: 4 }}>
              <Typography variant="body" color="primary" component="div" sx={{ fontWeight: 'bold' }}>
                {t('eos.landing_page.label.job_type')}
              </Typography>
              <Typography variant="body">
                {jobtype}
              </Typography>
            </Grid>
          </Grid>
        </Display>

        <Grid container item xs={12} sx={{ alignItems: 'flex-start' }}>
          <Grid
            item
            xs={4}
            sx={{
              display: 'flex', justifyContent: 'flex-end', pt: 2, pb: 4, pr: 3,
            }}
          >
            <AccountBalanceWalletOutlined color="primary" fontSize="large" />
          </Grid>
          <Grid
            item
            xs={8}
            sx={{
              bgcolor: theme.palette.grey['100'], pt: 2, pb: 4, px: 4,
            }}
          >
            <Typography variant="body" color="primary" component="div" sx={{ fontWeight: 'bold' }}>
              {t('eos.landing_page.label.salary')}
            </Typography>
            <Typography variant="body">
              {campaign.salaryValue()}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <header>
        <Header name={company.name} logo={company.logo} />
      </header>

      <main data-testid="page-main-section">

        {/* And thanks page and long form */}
        <Display if={showBanner()}>
          <CampaignBanner imageUrl={campaign.jobBanner} />
        </Display>

        <Display if={path === '' || path === 'webview'}>
          <Container>
            <Grid
              container
              item
              sx={{
                mt:                             8,
                [theme.breakpoints.down('sm')]: {
                  mt: 3,
                },
              }}
            >
              <Grid
                item
                md={8}
                xs={12}
                sx={{
                  mb:                             7,
                  pr:                             5,
                  [theme.breakpoints.down('sm')]: {
                    mb: 5,
                    pr: 0,
                  },
                }}
              >
                <Typography variant="h1" sx={{ fontWeight: 'medium', fontSize: '1.9rem' }}>
                  {campaign.title}
                </Typography>
              </Grid>
              <Grid container item md={4} xs={12} spacing={2} sx={{ alignItems: 'start', mb: 4, justifyContent: 'flex-end' }}>
                <Display if={apply !== 'false' && path !== 'webview'}>
                  <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      disableElevation
                      size="large"
                      onClick={() => navigate(applyLocation)}
                      sx={{
                        width:         '100%',
                        textTransform: 'inherit',
                        borderColor:   'primary.main',
                      }}
                    >
                      <Typography variant="body">{t('eos.landing_page.label.proceed')}</Typography>
                    </Button>
                  </Grid>
                </Display>
                <Grid item xs={6}>
                  <Button
                    onClick={() => setOpen(true)}
                    variant="outlined"
                    size="large"
                    endIcon={<Share />}
                    sx={{
                      width:         '100%',
                      textTransform: 'inherit',
                    }}
                  >
                    <Typography variant="body">{t('eos.landing_page.label.share')}</Typography>
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Container>

          <Container sx={{
            [theme.breakpoints.down('sm')]: {
              p: 0,
            },
          }}
          >
            <Grid container item sx={{ alignItems: 'flex-start' }}>
              <Display if={isScreenSM}>
                <JobInfo />
              </Display>

              <Grid
                item
                md={8}
                xs={12}
                sx={{
                  overflowWrap:                   'break-word',
                  pr:                             10,
                  [theme.breakpoints.down('sm')]: {
                    pr: 0,
                    px: 3,
                  },
                }}
              >
                <Typography
                  variant="body"
                  component="div"
                  sx={{
                    fontSize:   '1rem',
                    lineHeight: '1.5',
                  }}
                >
                  {ReactHtmlParser(campaign.description || job.description)}
                </Typography>
              </Grid>

              <Display if={apply !== 'false' && path !== 'webview'}>
                <Grid item xs={12} sx={{ my: 4, display: { xs: 'flex', md: 'none' }, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    disableElevation
                    size="large"
                    onClick={() => navigate(applyLocation)}
                    sx={{
                      width:         '50%',
                      textTransform: 'inherit',
                    }}
                  >
                    <Typography variant="body">{t('eos.landing_page.label.proceed')}</Typography>
                  </Button>
                </Grid>
              </Display>

              <Display if={!isScreenSM}>
                <JobInfo />
              </Display>
            </Grid>
          </Container>
        </Display>
      </main>

      <Display if={path === '' && company.description}>
        <article>
          <Container sx={{
            mt:                             8,
            [theme.breakpoints.down('sm')]: {
              mt:      4,
              p:       3,
              bgcolor: theme.palette.grey['100'],
            },
          }}
          >
            <Typography
              variant="h2"
              color="primary"
              sx={{
                fontWeight:                     'bold',
                mb:                             3,
                [theme.breakpoints.down('sm')]: {
                  textAlign: 'center',
                },
              }}
            >
              {`${t('eos.landing_page.text.about_company', { company: company.name })}`}
            </Typography>
            <Typography
              variant="body"
              component="div"
              sx={{
                fontSize:   '1rem',
                lineHeight: '1.5',
                a:          {
                  color:          'primary.main',
                  textDecoration: 'none',
                  '&:hover':      {
                    textDecoration: 'underline',
                  },
                },
              }}
            >
              {ReactHtmlParser(company.description)}
            </Typography>

            <Grid container>
              <Grid item md={8} sx={{ display: { xs: 'none', md: 'block' } }} />
              <Grid
                container
                item
                md={4}
                xs={12}
                spacing={2}
                sx={{
                  alignItems: 'start', mt: 8, justifyContent: 'flex-end', [theme.breakpoints.down('sm')]: { mt: 3 },
                }}
              >
                <Display if={apply !== 'false'}>
                  <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      disableElevation
                      size="large"
                      onClick={() => navigate(applyLocation)}
                      sx={{
                        width:         '100%',
                        textTransform: 'inherit',
                        borderColor:   'primary.main',
                      }}
                    >
                      <Typography variant="body">{t('eos.landing_page.label.proceed')}</Typography>
                    </Button>
                  </Grid>
                </Display>
                <Grid item xs={6}>
                  <Button
                    onClick={() => setOpen(true)}
                    variant="outlined"
                    size="large"
                    endIcon={<Share />}
                    sx={{
                      width:         '100%',
                      textTransform: 'inherit',
                    }}
                  >
                    <Typography variant="body">{t('eos.landing_page.label.share')}</Typography>
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </article>
      </Display>

      <Display if={path !== ''}>
        {children}
      </Display>

      {/* TODO: fixed covered footer when loading flash screen is showing */}
      <footer style={{ marginTop: 20 }}>
        <Footer name={company.name} logo={company.logo} showPrivacyPolicy={privacyPolicyLink} />
      </footer>

      <Display if={cookieNotification}>
        <CookieConsent
          location="bottom"
          buttonText="X"
          cookieName="talkpushCookies"
          style={{ background: '#28a745', color: '#000000', textAlign: 'center' }}
          buttonStyle={{
            background: '#FFFFFF', color: '#000000', fontSize: '13px', borderRadius: '50%',
          }}
          expires={150}
        >
          {t('eos.landing_page.consent')}
        </CookieConsent>
      </Display>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
      >
        <ModalBox sx={{
          maxWidth: '300px', px: 4, pt: 6, pb: 4,
        }}
        >
          <Grid container>
            <Button
              size="small"
              onClick={() => setOpen(false)}
              sx={{
                color: 'inherit', position: 'absolute', top: '15px', right: '0',
              }}
            >
              <CloseIcon />
            </Button>

            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Typography variant="body" component="div" sx={{ color: 'primary.main', fontWeight: 'bold', pb: 2 }}>{t('eos.landing_page.text.share_with_network')}</Typography>

              <SocialShare title={campaign.title} />
            </Grid>
          </Grid>
        </ModalBox>
      </Modal>
    </>
  );
}

const Interview = ({ children }) => children;

function LandingPageIndex() {
  const theme    = useTheme();
  const campaign = new CampaignRecord(window.LANDINGPAGE.campaign);
  const company  = new Company(window.LANDINGPAGE.company);

  const params = new URLSearchParams(window.location.search);

  if (
    (params.get('lead') == null || params.get('lead').length === 0)
    && (params.get('source') == null || params.get('source').length === 0)
    && window.LANDINGPAGE.source
  ) {
    params.set('source', window.LANDINGPAGE.source);
  }

  if ((params.get('redirect_url') == null || params.get('redirect_url').length === 0) && window.LANDINGPAGE.redirect_url) {
    params.set('redirect_url', window.LANDINGPAGE.redirect_url);
  }

  if ((params.get('refresh_rate') == null || params.get('refresh_rate').length === 0) && window.LANDINGPAGE.refresh_rate) {
    params.set('refresh_rate', window.LANDINGPAGE.refresh_rate);
  }

  const querystring = params.toString();
  const source      = params.get('source') || params.get('lead');

  return (
    <ApolloWrapper token={window.LANDINGPAGE.campaign.bearerToken}>
      <SnackbarProvider maxSnack={3} Components={{ highlight: HighlightNotification }}>
        <Router>
          <Page path="/site/campaigns/:bearer_token" theme={theme} querystring={querystring} campaign={campaign} company={company} apply={params.get('apply')}>
            <Apply path="apply" theme={theme} querystring={querystring} source={source} campaign={campaign} company={company} />
            <Interview path="candidate/:token">
              <Candidate path="/" theme={theme} campaign={campaign} company={company} />
              <Completion
                path="completed"
                theme={theme}
                job={campaign.title}
                message={campaign.settings.completionMessage}
                ctas={campaign.settings.completionCta}
                redirectUrl={params.get('redirect_url')}
                refreshRate={params.get('refresh_rate')}
              />
            </Interview>
          </Page>
        </Router>
      </SnackbarProvider>
    </ApolloWrapper>
  );
}

Page.propTypes = {
  '*':      PropTypes.string,
  navigate: PropTypes.func,
  children: PropTypes.node,
  uri:      PropTypes.string,
  company:  PropTypes.object,
};

export default LandingPageIndex;
